import { ApartmentOutlined, ContactsFilled, EnvironmentFilled, FileDoneOutlined, SyncOutlined } from '@ant-design/icons'
import optimizerIcon from '@assets/CustomIcons/optimizerIcon.svg'
import { BBDTag, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Spin } from 'antd'
import classNames from 'classnames'
import { useEffect, useMemo } from 'react'
import { ROUTE_OPTIMIZER_MODE } from './types'

export function HistoryPanel({
  setSelectedRun,
  selectedRun,
  mode,
  setMode,
  loading,
  data,
  isFetching,
  lockedOrders,
  lockedSchedules,
  updatedItems,
}) {
  const pendingCards = useMemo(
    () =>
      updatedItems
        ?.filter((card) => card.state === 'pending')
        .sort((a, b) => new Date(b.created_on) - new Date(a.created_on)),
    [updatedItems]
  )

  const otherCards = useMemo(
    () =>
      updatedItems
        ?.filter((card) => card.state !== 'pending')
        .sort((a, b) => new Date(b.created_on) - new Date(a.created_on)),
    [updatedItems]
  )

  useEffect(() => {
    if (!selectedRun && data?.length && mode === 'REVIEW') {
      if (pendingCards?.length > 0) setSelectedRun(pendingCards[pendingCards.length - 1])
      else {
        setSelectedRun(data?.[data.length - 1])
      }
    }
  }, [selectedRun, data, mode])

  if (isFetching || loading === 'creating') {
    return (
      <Vertical verticalCenter>
        <Spin size='large' />
      </Vertical>
    )
  }
  if (!updatedItems) return <Texto category='h3'>No Runs Yet!</Texto>
  return (
    <Vertical>
      <Vertical border='all'>
        <div>
          <Horizontal className='p-3'>
            <img src={optimizerIcon} alt='optimizer icon' />
            <Vertical className='ml-4'>
              <Texto weight='bold' category='h3'>
                Route Optimizer
              </Texto>
              <Texto>View previous runs or start a new one.</Texto>
            </Vertical>
          </Horizontal>
        </div>
        <div style={{ overflowY: 'auto', maxHeight: '1010px' }}>
          <div style={{ backgroundColor: 'var(--theme-success)' }}>
            {pendingCards?.map((card) => (
              <PendingReviewCard
                key={card.id}
                card={card}
                setSelectedRun={setSelectedRun}
                selectedRun={selectedRun}
                setMode={setMode}
              />
            ))}
          </div>

          <div style={{ backgroundColor: 'var(--bg-2' }}>
            <Horizontal border='all' className='p-3'>
              <Texto>RUN HISTORY</Texto>
            </Horizontal>
          </div>
          {otherCards.length > 0 &&
            otherCards?.map((card) => (
              <HistoryCard
                key={card.id}
                card={card}
                selectedRun={selectedRun}
                setSelectedRun={setSelectedRun}
                lockedOrders={lockedOrders}
                lockedSchedules={lockedSchedules}
                setMode={setMode}
              />
            ))}
        </div>
      </Vertical>

      <Horizontal className='bg-2 mb-4' horizontalCenter alignItems='end'>
        <GraviButton
          theme2
          className='optimizer-run-button'
          buttonText='Create New Run'
          onClick={() => {
            setSelectedRun(null)
            setMode(ROUTE_OPTIMIZER_MODE.CREATE)
          }}
        />
      </Horizontal>
    </Vertical>
  )
}

function HistoryCard({ card, setSelectedRun, selectedRun, lockedOrders, lockedSchedules, setMode }) {
  const isSelected = useMemo(() => selectedRun?.id === card.id, [selectedRun, card])
  const tagBorder =
    card?.state === 'in progress'
      ? '1px solid var(--theme-color-2)'
      : card.state === 'accepted'
        ? '1px solid var(--theme-success)'
        : card.state === 'rejected'
          ? '1px solid var(--theme-error)'
          : '1px solid var(--theme-option)'
  const selectOrderCount = useMemo(() => lockedOrders?.filter((o) => o.run_id === card.id)?.length, [lockedOrders])
  const selectScheduleCount = useMemo(
    () => lockedSchedules?.filter((sched) => sched.run_id === card.id)?.length,
    [lockedSchedules]
  )
  const displayDate = useMemo(
    () =>
      card.state !== 'canceled'
        ? formatters.SHORT_DATE_TIME(card.created_on)
        : formatters.SHORT_DATE_TIME(card.canceled_on),
    [card?.state]
  )

  return (
    <div
      key={card.id}
      className={classNames('bg-1', {
        'optimizer-history-card': card.state === 'accepted' || card.state === 'rejected',
      })}
      style={{ border: isSelected ? '1px solid var(--theme-color-1)' : 'solid 1px var(--gray-200)' }}
      onClick={() => {
        if (card.state === 'building') {
          setMode(ROUTE_OPTIMIZER_MODE.EDIT)
        } else {
          setMode(ROUTE_OPTIMIZER_MODE.REVIEW)
        }
        setSelectedRun(card)
      }}
    >
      <Horizontal className='pr-4 pl-4 pt-2 pb-2' verticalCenter>
        <Vertical flex={1} alignItems='start' className='mr-1'>
          <Texto category='label'>{displayDate}</Texto>
        </Vertical>
        <Vertical flex={1} verticalCenter className='mr-1'>
          <Texto appearance='secondary'>
            <FileDoneOutlined />
            {card.state === 'building'
              ? ` ${selectOrderCount} | ${selectScheduleCount} `
              : ` ${card.total_orders} | ${card.total_schedules} `}
            <ContactsFilled />
          </Texto>
        </Vertical>
        <Vertical flex={1}>
          <BBDTag
            theme2={card.state === 'in progress'}
            success={card.state === 'accepted'}
            error={card.state === 'rejected' || card.state === 'error'}
            warning={card.state === 'building'}
            className='text-truncate'
            style={{ border: tagBorder, margin: 0, fontSize: '1em', textTransform: 'capitalize' }}
          >
            {card.state === 'in progress' && <SyncOutlined rotate={90} className='mr-1' />}
            {card.state === 'in progress' ? 'Optimizing' : card.state}
          </BBDTag>
        </Vertical>
      </Horizontal>

      <Horizontal verticalCenter className='pl-4 pr-4 pb-2 pt-2'>
        <Vertical alignItems='start' flex={1} className='mr-1'>
          <Texto className='text-truncate' style={{ width: '100%' }}>
            <ApartmentOutlined />{' '}
            {card.markets.length > 1 ? `${card.markets.length} Markets` : card.markets[0] || 'N/A'}
          </Texto>
        </Vertical>

        <Vertical flex={1} className='mr-2'>
          <Texto className='text-truncate' style={{ width: '100%' }}>
            <EnvironmentFilled /> {card.depots.length > 1 ? `${card.depots.length} Depots` : card.depots[0] || 'N/A'}
          </Texto>
        </Vertical>
        <Vertical flex={1} alignItems='end'>
          <Texto className='text-truncate' style={{ width: '100%' }}>
            by {card.state !== 'canceled' ? card.created_by : card.canceled_by}
          </Texto>
        </Vertical>
      </Horizontal>
    </div>
  )
}

function PendingReviewCard({ card, setSelectedRun, selectedRun, setMode }) {
  const isSelected = useMemo(() => selectedRun?.id === card.id, [selectedRun, card])
  const border = isSelected ? '1px solid var(--theme-color-1)' : 'solid 1px var(--gray-200)'
  return (
    <Horizontal className='p-3' style={{ border }}>
      <Vertical verticalCenter alignItems='start' flex={1} className='mr-2'>
        <Texto appearance='white' align='left' className='text-truncate' style={{ width: '100%' }}>
          Run {card.name} Complete
        </Texto>
        <Texto appearance='white' className='text-truncate mr-2' style={{ width: '100%' }}>
          {formatters.SHORT_DATE_TIME(card.created_on)}
        </Texto>
      </Vertical>

      <Vertical verticalCenter alignItems='start' flex={1} className='mr-1'>
        <Texto align='left' appearance='white' className='text-truncate' style={{ width: '100%' }} />
        <Texto appearance='white' align='left' className='text-truncate' style={{ width: '100%' }}>
          {card.created_by}
        </Texto>
      </Vertical>

      <Vertical verticalCenter flex={1}>
        <GraviButton
          style={{ padding: 0 }}
          buttonText='Review Results'
          onClick={() => {
            setMode(ROUTE_OPTIMIZER_MODE.REVIEW)
            setSelectedRun(card)
          }}
        />
      </Vertical>
    </Horizontal>
  )
}
